import { currentLang, getAccessToken, signOutFromWeb } from '../actions/authActions';
import { isPikError } from '../../types/pikError';
import { generateSearchParams, IParams } from './createParams';
import { notFound } from 'next/navigation';

export default class HttpCLient {
  private baseUrl = process.env.NEXT_PUBLIC_PIK_BACKEND_URL;

  private async handleRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}/${endpoint}`;

    const token = await getAccessToken();
    const lang = await currentLang();

    try {
      const requestOptions: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          tenant: 'pik',
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(lang && { 'Accept-Language': lang }),
        },
        ...(options.body && { body: JSON.stringify(options.body) }),
        method: options.method,
      };

      const response = await fetch(url, requestOptions);

      if (response.status === 404) notFound();

      if (response.status === 401) {
        // await revokeKeycloakToken();
        // await signOut({ callbackUrl: '/' });
        await signOutFromWeb();
      }

      if (response.status === 204) {
        // No content to return, indicate success
        return {} as T;
      }

      const contentLength = response.headers.get('Content-Length');
      const text = await response.text();

      if (response.status === 200 && (contentLength === '0' || !text)) {
        return {} as T; // Return empty object for empty body with 200
      }

      if (!response.ok) {
        // Try to parse the response as PikError
        const errorResponse = JSON.parse(text);
        if (isPikError(errorResponse)) {
          // Throw a meaningful PikError with details from the response
          // throw new Error(`${errorResponse.title}: ${errorResponse.detail} `);
          throw new Error(` ${errorResponse.detail} `);
        }

        // If not a PikError, throw the default response status text
        throw new Error(response.statusText);
      }

      // Parse the JSON response
      return JSON.parse(text);
    } catch (error) {
      // Handle the error further if needed (log, re-throw, etc.)
      if (error instanceof Error) {
        console.error('Request failed:', error.message);
      }

      throw error;
    }
  }

  //   get function

  protected async get<T>(
    endpoint: string,
    params?: IParams,
    options: RequestInit = {},
  ): Promise<T> {
    let url = `${endpoint}`;
    if (params) {
      const searchParams = generateSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    return this.handleRequest<T>(url, options);
  }

  //   post request

  protected async post<T>(endpoint: string, data: any, options?: RequestInit): Promise<any> {
    const requestOptions: RequestInit = {
      ...options,
      method: 'POST',
      body: data,
    };
    return this.handleRequest<T>(endpoint, requestOptions);
  }

  //delete request
  protected async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const requestOptions: RequestInit = {
      ...options,
      method: 'DELETE',
    };
    return this.handleRequest<T>(endpoint, requestOptions);
  }

  //   put request
  protected async put<T>(endpoint: string, data: any, options?: RequestInit): Promise<any> {
    const requestOptions: RequestInit = {
      ...options,
      method: 'PUT',
      body: data,
    };
    return this.handleRequest<T>(endpoint, requestOptions);
  }
}
